/**
 * Supported Languages Configuration
 * 
 * Defines all supported languages with metadata for the language selector.
 * Languages are organized by priority tier but presented as a flat list.
 */

export interface LanguageConfig {
  /** ISO 639-1 code */
  code: string;
  /** English name */
  name: string;
  /** Native name in the language itself */
  nativeName: string;
  /** Writing direction */
  dir: 'ltr' | 'rtl';
  /** Priority tier for sorting (1 = first class, 2 = second, 3 = third) */
  tier: 1 | 2 | 3;
}

export const SUPPORTED_LANGUAGES: LanguageConfig[] = [
  // Tier 1 — First class
  { code: 'en', name: 'English',    nativeName: 'English',    dir: 'ltr', tier: 1 },
  { code: 'ru', name: 'Russian',    nativeName: 'Русский',    dir: 'ltr', tier: 1 },
  { code: 'fr', name: 'French',     nativeName: 'Français',   dir: 'ltr', tier: 1 },
  { code: 'es', name: 'Spanish',    nativeName: 'Español',    dir: 'ltr', tier: 1 },
  { code: 'de', name: 'German',     nativeName: 'Deutsch',    dir: 'ltr', tier: 1 },

  // Tier 2 — European & main Asian
  { code: 'pt', name: 'Portuguese', nativeName: 'Português',  dir: 'ltr', tier: 2 },
  { code: 'it', name: 'Italian',    nativeName: 'Italiano',   dir: 'ltr', tier: 2 },
  { code: 'nl', name: 'Dutch',      nativeName: 'Nederlands', dir: 'ltr', tier: 2 },
  { code: 'pl', name: 'Polish',     nativeName: 'Polski',     dir: 'ltr', tier: 2 },
  { code: 'uk', name: 'Ukrainian',  nativeName: 'Українська', dir: 'ltr', tier: 2 },
  { code: 'sv', name: 'Swedish',    nativeName: 'Svenska',    dir: 'ltr', tier: 2 },
  { code: 'el', name: 'Greek',      nativeName: 'Ελληνικά',   dir: 'ltr', tier: 2 },
  { code: 'tr', name: 'Turkish',    nativeName: 'Türkçe',     dir: 'ltr', tier: 2 },
  { code: 'ko', name: 'Korean',     nativeName: '한국어',       dir: 'ltr', tier: 2 },
  { code: 'th', name: 'Thai',       nativeName: 'ไทย',         dir: 'ltr', tier: 2 },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', dir: 'ltr', tier: 2 },
  { code: 'hi', name: 'Hindi',      nativeName: 'हिन्दी',       dir: 'ltr', tier: 2 },

  // Tier 3 — Special case languages (RTL text handled inline, layout stays LTR)
  { code: 'he', name: 'Hebrew',     nativeName: 'עברית',      dir: 'ltr', tier: 3 },
  { code: 'ar', name: 'Arabic',     nativeName: 'العربية',     dir: 'ltr', tier: 3 },
  { code: 'zh', name: 'Chinese',    nativeName: '中文',        dir: 'ltr', tier: 3 },
  { code: 'ja', name: 'Japanese',   nativeName: '日本語',      dir: 'ltr', tier: 3 },
];

/** Get language config by code */
export function getLanguage(code: string): LanguageConfig | undefined {
  return SUPPORTED_LANGUAGES.find(l => l.code === code);
}

/** Get all language codes */
export function getLanguageCodes(): string[] {
  return SUPPORTED_LANGUAGES.map(l => l.code);
}
